import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PawPrint, Image as ImgIcon, Scissors, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ShareGallery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/pets/${token}/gallery`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Gallery unavailable"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen grid place-content-center bg-[#FAF7F2] text-[#6B625B]">Loading…</div>;
  if (error) {
    return (
      <div className="min-h-screen grid place-content-center bg-[#FAF7F2] p-6 text-center">
        <div className="max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-[#F5E6CF] grid place-content-center mx-auto text-[#A9480F] mb-4">
            <ImgIcon className="w-6 h-6"/>
          </div>
          <h1 className="font-display text-2xl font-bold">Gallery unavailable</h1>
          <p className="text-sm text-[#6B625B] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const photos = data.photos || [];
  const pairs = [];
  const singles = [];
  const used = new Set();
  const sorted = [...photos].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const p = sorted[i];
    if (p.photo_label === "before") {
      const partner = sorted.findIndex((q, j) =>
        j > i && !used.has(j) && q.photo_label === "after" &&
        (new Date(q.created_at) - new Date(p.created_at)) < 4 * 3600 * 1000
      );
      if (partner !== -1) {
        pairs.push({ before: p, after: sorted[partner] });
        used.add(i); used.add(partner);
        continue;
      }
    }
    singles.push(p);
    used.add(i);
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1A17]">
      <header className="border-b border-[#E5DCCE] bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white">
              <Scissors className="w-4 h-4"/>
            </div>
            <div>
              <div className="font-display font-bold leading-none">PawTrek</div>
              <div className="text-[10px] text-[#6B625B] uppercase tracking-wider mt-1">Mobile Grooming</div>
            </div>
          </div>
          <span className="pill bg-[#F5E6CF] text-[#A9480F] border border-amber-900/10 text-[10px]">
            Public gallery
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        <div className="flex items-center gap-4">
          {data.pet.photo_url ? (
            <img src={data.pet.photo_url} alt={data.pet.name} className="w-16 h-16 rounded-2xl object-cover"/>
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-[#F5E6CF] grid place-content-center text-[#A9480F]">
              <PawPrint className="w-7 h-7"/>
            </div>
          )}
          <div>
            <div className="label-accent">Meet</div>
            <h1 className="font-display text-3xl font-bold mt-1" data-testid="share-pet-name">{data.pet.name}</h1>
            <div className="text-sm text-[#6B625B]">{data.pet.breed} · {photos.length} photo{photos.length===1?"":"s"}</div>
          </div>
        </div>

        {photos.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#E5DCCE] p-10 text-center text-[#6B625B]">
            <ImgIcon className="w-8 h-8 mx-auto mb-2 opacity-40"/>
            <p className="text-sm">No photos yet. Check back after the next groom!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pairs.length > 0 && (
              <section>
                <h2 className="font-display text-xl font-bold mb-3">Before & after</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {pairs.map((p, i) => (
                    <div key={i} className="bg-white rounded-3xl border border-[#E5DCCE] p-3 shadow-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <SharePic photo={p.before} onOpen={() => setOpenIdx(p.before)} tint="amber"/>
                        <SharePic photo={p.after} onOpen={() => setOpenIdx(p.after)} tint="emerald"/>
                      </div>
                      <div className="text-[11px] text-[#6B625B] mt-2 text-center">
                        {new Date(p.after.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {singles.length > 0 && (
              <section>
                <h2 className="font-display text-xl font-bold mb-3">More snaps</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {singles.map((p) => (
                    <SharePic key={p.id} photo={p} onOpen={() => setOpenIdx(p)}
                      tint={p.photo_label === "before" ? "amber" : p.photo_label === "after" ? "emerald" : "neutral"}/>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        <div className="pt-6 border-t border-[#E5DCCE] flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs text-[#6B625B]">Groomed with love by PawTrek Mobile Grooming</span>
          <a href={window.location.origin} target="_blank" rel="noreferrer" data-testid="footer-cta">
            <Button variant="outline" size="sm" className="rounded-full">
              Book your pup <ExternalLink className="w-3 h-3 ml-1"/>
            </Button>
          </a>
        </div>
      </main>

      <Dialog open={!!openIdx} onOpenChange={() => setOpenIdx(null)}>
        <DialogContent className="max-w-3xl bg-transparent border-0 shadow-none p-0">
          {openIdx && (
            <img src={`${API}${openIdx.photo_url.replace('/api','')}`}
              alt="" className="w-full h-auto rounded-2xl"/>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SharePic({ photo, onOpen, tint }) {
  const tintClass = {
    amber: "bg-amber-200 text-amber-900",
    emerald: "bg-emerald-200 text-emerald-900",
    neutral: "bg-stone-200 text-stone-800",
  }[tint];
  return (
    <button onClick={onOpen}
      className="relative aspect-square rounded-2xl overflow-hidden bg-[#F3EDE4] card-tactile"
      data-testid={`share-photo-${photo.id}`}>
      <img src={`${API}${photo.photo_url.replace('/api','')}`} alt=""
        className="w-full h-full object-cover"/>
      {photo.photo_label && (
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tintClass}`}>
          {photo.photo_label}
        </span>
      )}
    </button>
  );
}
